import { FC, useEffect, useState } from 'react';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { webEnvConfig } from '@config';
import { keycloakGoogleSSOLoginURL, keycloakRealmLoginURL, useKeycloakOAuthCallback } from '@features/auth';
import { useDirection } from '@hooks';
import { Trans, useTrans } from '@libs/i18n';

export const Login: FC = observer(() => {
  const { isLoading, setIsLoading } = useKeycloakOAuthCallback();
  const { i18n } = useTrans();
  const { isRtl } = useDirection();
  const [activeButton, setActiveButton] = useState<'signIn' | 'google' | null>(null);

  const anyLoading = isLoading || activeButton !== null;
  // After the Keycloak redirect back, the page reloads (activeButton resets to
  // null) while the backend validation request runs — show the button spinner
  // during that window so the user knows we're processing their login.
  const callbackProcessing = isLoading && activeButton === null;

  // When the user clicks an SSO button we redirect to Keycloak/Google and keep
  // the button spinner on. If they hit the browser Back button without picking
  // an account, the page is restored from the bfcache with React state intact —
  // so the spinner would stay on forever. `pageshow` with `event.persisted`
  // fires on that restore; reset the loading state so the buttons are usable.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setActiveButton(null);
        setIsLoading(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [setIsLoading]);

  const handleSignIn = () => {
    setActiveButton('signIn');
    setIsLoading(true);
    keycloakRealmLoginURL(i18n.language);
  };

  const handleGoogleLogin = () => {
    setActiveButton('google');
    setIsLoading(true);
    keycloakGoogleSSOLoginURL(i18n.language);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: { xs: "center", md: "flex-start" },
        alignItems: { xs: "center", md: "stretch" },
        width: '100%',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {/* ── Left hero pane ── */}
      <Box
        sx={{
          flex: { xs: "none", md: '0 0 60%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: "flex-end", md: 'center' },
          alignItems: { xs: "center", md: "flex-start" },
          paddingInlineStart: { xs: 0, md: 10 },
          paddingBottom: { xs: "2rem", md: 0 },
          position: { xs: "absolute", md: 'relative' },
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 0,
          overflow: 'hidden',
          pointerEvents: "none",
        }}
      >
        <Box
          component="img"
          src="/login-background.jpg"
          alt=""
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: { xs: "30%", md: '50%' },
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: { xs: "120%", md: '740px' },
            height: 'auto',
            objectFit: 'contain',
            aspectRatio: '1/1',
            opacity: { xs: 0.15, md: 0.3 },
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
        <Box sx={{ zIndex: 1, display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 16 }, width: { xs: "90%", md: '80%' }, marginInlineStart: { xs: 0, md: 18 }, textAlign: { xs: "center", md: "start" } }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 1, md: 3 }, alignItems: 'center', justifyContent: { xs: "center", md: "flex-start" }, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ color: '#707070', fontWeight: 500, fontSize: { xs: "0.875rem", md: "1.25rem" }, textShadow: { xs: "0 1px 2px rgba(255,255,255,0.8)", md: "none" } }}>
              <Trans i18nKey="auth.login.description1" />
            </Typography>
            <Box sx={{ width: { xs: "10%", md: '15%' }, minWidth: { xs: 20, md: 40 }, height: '2px', backgroundColor: '#707070', flexShrink: 0 }} />
            <Typography variant="h6" sx={{ color: '#707070', fontWeight: 500, fontSize: { xs: "0.875rem", md: "1.25rem" }, textShadow: { xs: "0 1px 2px rgba(255,255,255,0.8)", md: "none" } }}>
              <Trans i18nKey="auth.login.description2" />
            </Typography>
          </Box>
          <Typography
            variant="h2"
            sx={{ fontWeight: { xs: 700, md: 500 }, fontSize: { xs: "1.5rem", md: '3rem', lg: '3.75rem' }, lineHeight: { xs: "2rem", md: '3.5rem', lg: '4.5rem' }, marginInlineStart: { xs: 0, md: 5 }, textShadow: { xs: "0 1px 3px rgba(255,255,255,0.8)", md: "none" } }}
          >
            <Trans i18nKey="auth.login.title" />
          </Typography>
        </Box>
      </Box>

      {/* ── Right form pane ── */}
      <Box
        sx={{
          flex: { xs: "none", md: '0 0 40%' },
          width: { xs: "100%", sm: "85%", md: "auto" },
          maxWidth: { xs: "400px", md: "none" },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: { xs: '2rem', md: '80px 60px' },
          minHeight: { xs: 'auto', md: '100vh' },
          position: "relative",
          zIndex: 1,
          mt: { xs: "4rem", md: 0 },
          mb: { xs: "8rem", md: 0 },
          bgcolor: { xs: "rgba(255, 255, 255, 0.8)", md: "transparent" },
          backdropFilter: { xs: "blur(12px)", md: "none" },
          borderRadius: { xs: 4, md: 0 },
          boxShadow: { xs: "0 8px 32px rgba(0,0,0,0.08)", md: "none" },
          border: { xs: "1px solid rgba(255,255,255,0.5)", md: "none" },
          pointerEvents: "auto",
        }}
      >
        <Stack spacing={2} sx={{ width: '100%', maxWidth: { xs: '100%', sm: '452px' } }}>
          {/* Logo + title + description */}
          <Stack spacing={1} alignItems="center">
            <Box
              component="a"
              href={webEnvConfig.websiteUrl}
              aria-label="Go to Komplyance home"
              sx={{
                border: '1px solid #E5E4E4C4',
                borderRadius: '12px',
                width: '62px',
                height: '62px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                textDecoration: 'none',
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: '#0a0a0a' },
              }}
            >
              <Box component="img" src="/logo.svg" alt="Komplyance" sx={{ width: '53px', height: '45px', objectFit: 'contain' }} />
            </Box>
            <Typography variant="h5" fontWeight={600} textAlign="center">
              <Trans i18nKey="auth.internalLogin.form.title" />
            </Typography>
            <Typography variant="body2" textAlign="center" sx={{ color: '#8F8F8F', maxWidth: '360px' }}>
              <Trans i18nKey="auth.internalLogin.form.description" />
            </Typography>
          </Stack>

          {/* Sign In — Keycloak realm */}
          <Button
            onClick={handleSignIn}
            disabled={anyLoading}
            variant="contained"
            size="large"
            fullWidth
            sx={{
              borderRadius: '12px',
              minHeight: '45px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              gap: '8px',
              backgroundColor: '#0a0a0a',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#333', boxShadow: 'none' },
            }}
          >
            {(activeButton === 'signIn' || callbackProcessing) && (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            )}
            <Trans i18nKey="auth.internalLogin.form.button.signIn" />
          </Button>

          <Divider sx={{ color: '#8F8F8F', fontSize: '0.8125rem' }}>Or</Divider>

          {/* Continue with Google */}
          <Button
            onClick={handleGoogleLogin}
            disabled={anyLoading}
            fullWidth
            sx={{
              minHeight: '45px',
              borderRadius: '12px',
              border: '1px solid #8F8F8F',
              padding: '12px 20px',
              gap: '8px',
              textTransform: 'none',
              backgroundColor: 'transparent',
              color: 'text.primary',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#333', boxShadow: 'none' },
            }}
          >
            {activeButton === 'google' || callbackProcessing ? (
              <CircularProgress size={20} sx={{ color: '#8F8F8F' }} />
            ) : (
              <Box component="img" src="/Google.svg" alt="" aria-hidden="true" sx={{ width: '20px', height: '20px' }} />
            )}
            <Typography variant="body1" fontWeight={500}>
              <Trans i18nKey="auth.login.form.button.google" />
            </Typography>
          </Button>

          {/* Back to marketing site — subtle ghost pill so it clearly reads as a back action */}
          <Button
            component="a"
            href={webEnvConfig.websiteUrl}
            disableRipple
            startIcon={
              <ArrowBackRoundedIcon
                sx={{ fontSize: '1.05rem', transform: isRtl ? 'scaleX(-1)' : 'none' }}
              />
            }
            sx={{
              alignSelf: 'center',
              mt: 1,
              px: 2,
              py: 0.6,
              backgroundColor: 'transparent',
              color: '#8F8F8F',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              borderRadius: '999px',
              transition: 'background-color 0.2s, color 0.2s',
              '&:hover': { backgroundColor: '#F4F4F5', color: '#0a0a0a' },
            }}
          >
            <Trans i18nKey="auth.internalLogin.backToHome" />
          </Button>
        </Stack>
      </Box>
    </Box>
  );
});
